import React from 'react';
import { useGame } from '../state/gameStore.tsx';
import { MetalIcon, He3Icon, CreditsIcon } from '../assets/icons.tsx';

export const Header: React.FC = () => {
  const { state, isDemo } = useGame();

  if (!state) return null;

  const { player, planet } = state;
  const { metal, he3, credits, max_metal, max_he3, metal_rate_hour, he3_rate_hour, credits_rate_hour } = planet.resources;

  const isNebulaFrame = player.equipped_galaxy_deco === 'deco_galaxy_nebula_frame' || player.equipped_cosmetics?.galaxy_deco === 'deco_galaxy_nebula_frame';

  return (
    <header style={{
      position: 'sticky',
      top: 0,
      zIndex: 100,
      background: 'rgba(5, 8, 17, 0.95)',
      backdropFilter: 'blur(8px)',
      borderBottom: '1px solid var(--border-panel)',
      paddingTop: 'calc(8px + env(safe-area-inset-top, 0px))',
      paddingBottom: 8,
      paddingLeft: 12,
      paddingRight: 12,
    }}>
      {/* Top line: Commander Info & Planet Coordinates */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{
            background: isNebulaFrame
              ? 'linear-gradient(135deg, rgba(234, 179, 8, 0.25) 0%, rgba(202, 138, 4, 0.2) 100%)'
              : 'rgba(0, 229, 255, 0.15)',
            border: isNebulaFrame ? '1px solid #fde047' : '1px solid var(--accent-cyan)',
            boxShadow: isNebulaFrame ? '0 0 10px rgba(234, 179, 8, 0.5)' : undefined,
            padding: '2px 8px',
            borderRadius: 4,
            fontSize: 12,
            fontWeight: 700,
            color: isNebulaFrame ? '#fde047' : 'var(--accent-cyan)',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}>
            {isNebulaFrame ? '✨ HOÀNG KIM' : (isDemo ? 'DEV DEMO' : 'TMA PRO')}
          </div>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-bright)' }}>
            {player.username}
          </span>
          {player.is_vip && (
            <span style={{
              background: 'linear-gradient(135deg, #eab308 0%, #ca8a04 100%)',
              color: '#050811',
              fontSize: 10,
              fontWeight: 800,
              padding: '2px 6px',
              borderRadius: 3,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
            }}>
              👑 VIP
            </span>
          )}
        </div>

        <div className="mono" style={{
          fontSize: 11,
          color: 'var(--accent-cyan)',
          background: 'rgba(23, 42, 84, 0.4)',
          padding: '2px 6px',
          borderRadius: 3,
          maxWidth: '55%',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {planet.name} [{planet.coord_x}:{planet.coord_y}]
        </div>
      </div>

      {/* Resource Badges */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
        {/* Metal */}
        <div style={{
          background: 'var(--bg-panel-subtle)',
          border: '1px solid var(--border-panel)',
          borderRadius: 4,
          padding: '4px 6px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <MetalIcon size={14} />
              <span style={{ fontSize: 11, color: 'var(--text-dim)', fontWeight: 600 }}>KIM LOẠI</span>
            </div>
            <span className="mono" style={{ fontSize: 10, color: 'var(--status-green)' }}>
              +{Math.round(metal_rate_hour)}/h
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <span className="mono" style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-bright)' }}>
              {Math.floor(metal).toLocaleString()}
            </span>
            <span className="mono" style={{ fontSize: 10, color: 'var(--text-dim)' }}>
              /{Math.floor(max_metal).toLocaleString()}
            </span>
          </div>
          <div className="sci-progress" style={{ marginTop: 3 }}>
            <div
              className="sci-progress-bar"
              style={{ width: `${Math.min(100, (metal / max_metal) * 100)}%` }}
            />
          </div>
        </div>

        {/* He-3 */}
        <div style={{
          background: 'var(--bg-panel-subtle)',
          border: '1px solid var(--border-panel)',
          borderRadius: 4,
          padding: '4px 6px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <He3Icon size={14} />
              <span style={{ fontSize: 11, color: 'var(--text-dim)', fontWeight: 600 }}>HELI-3</span>
            </div>
            <span className="mono" style={{ fontSize: 10, color: 'var(--status-green)' }}>
              +{Math.round(he3_rate_hour)}/h
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <span className="mono" style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent-cyan)' }}>
              {Math.floor(he3).toLocaleString()}
            </span>
            <span className="mono" style={{ fontSize: 10, color: 'var(--text-dim)' }}>
              /{Math.floor(max_he3).toLocaleString()}
            </span>
          </div>
          <div className="sci-progress" style={{ marginTop: 3 }}>
            <div
              className="sci-progress-bar"
              style={{ width: `${Math.min(100, (he3 / max_he3) * 100)}%` }}
            />
          </div>
        </div>

        {/* Credits */}
        <div style={{
          background: 'var(--bg-panel-subtle)',
          border: '1px solid var(--border-panel)',
          borderRadius: 4,
          padding: '4px 6px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <CreditsIcon size={14} />
              <span style={{ fontSize: 11, color: 'var(--text-dim)', fontWeight: 600 }}>TÍN DỤNG</span>
            </div>
            <span className="mono" style={{ fontSize: 10, color: 'var(--status-green)' }}>
              +{Math.round(credits_rate_hour)}/h
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <span className="mono" style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent-amber)' }}>
              {Math.floor(credits).toLocaleString()}
            </span>
          </div>
          <div className="sci-progress" style={{ marginTop: 3 }}>
            <div className="sci-progress-bar sci-progress-bar-amber" style={{ width: '100%' }} />
          </div>
        </div>
      </div>
    </header>
  );
};
